import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import styles from "./AttachmentList.module.css";
import { getIconForContentType } from "../utils/getIconForContentType";
import PDFViewer from "../utils/PDFViewer";
import { createWrappedMessage, getBinaryResourcesFromDocumentReference, buildAgentFromContextProvider, buildEntityFromContextPatient } from "../utils/messageUtils";
import { buildErrorAuditEvent } from "../services/auditEventBuilder";
import { logEvent } from "../services/logService";

// Utility: Map MIME type to extension
const getExtensionFromContentType = (contentType) => {
    console.log("Getting extension for content type:", contentType);
    const map = {
        "application/pdf": "pdf",
        "image/jpeg": "jpg",
        "image/png": "png",
        "image/gif": "gif",
        "image/webp": "webp",
        "image/bmp": "bmp",
        "text/plain": "txt",
        "text/html": "html",
        "application/json": "json",
        "application/xml": "xml",
        "application/msword": "doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx"
    };
    return map[contentType] || "dat";
};

const handleDownload = async (
    client,
    contentType,
    url,
    title = "document"
) => {
    try {
        const knownMimeTypes = {
            pdf: "application/pdf",
            txt: "text/plain",
            csv: "text/csv",
            log: "text/plain",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            png: "image/png",
            html: "text/html",
            htm: "text/html"
        };

        console.log(contentType);
        const extension = getExtensionFromContentType(contentType); // your shared utility
        console.log(extension);
        let filename = title;

        if (!filename.endsWith(`.${extension}`)) {
            filename += `.${extension}`;
        }

        const acceptHeader = knownMimeTypes[extension] || contentType;

        const response = await client.request(url, {
            headers: { Accept: acceptHeader },
            fetchResponse: true
        });

        let blob;

        if (response instanceof Response && typeof response.blob === "function") {
            blob = await response.blob();
        } else if (typeof response === "string") {
            blob = new Blob([response], { type: acceptHeader });
        } else {
            throw new Error("Unsupported response format for download.");
        }

        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
        console.error("Download error:", error);
    }
};
const handleOpen = async (
    client,
    contentType,
    url,
    title = "document",
    setModalSrc,
    setModalTitle,
    setModalType,
    setShowModal,
    contextProvider,
    contextPatient
) => {
    try {
        const extension = getExtensionFromContentType(contentType);
        let acceptHeader = contentType || "application/octet-stream";
        let type = "generic";

        if (extension === "pdf") type = "pdf";
        else if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)) type = "image";
        else if (["txt", "csv", "log"].includes(extension)) type = "text";
        else if (["html", "htm"].includes(extension)) type = "html";

        const response = await client.request(url, {
            headers: { Accept: acceptHeader },
            fetchResponse: true
        });

        console.log("Open response:", response);

        let blob;

        if (response instanceof Response && typeof response.blob === "function") {
            blob = await response.blob();
        } else if (typeof response === "string") {
            blob = new Blob([response], { type: contentType || "text/plain" });
        } else {
            throw new Error("Unsupported response format for preview.");
        }

        const blobUrl = window.URL.createObjectURL(blob);

        setModalSrc(blobUrl);
        setModalTitle(title);
        setModalType(type);
        setShowModal(true);
    } catch (error) {
        console.error("Open preview error:", error);

        try {
            const agent = buildAgentFromContextProvider(contextProvider);
            const entity = buildEntityFromContextPatient(contextPatient);

            const auditEvent = buildErrorAuditEvent({
                errorType: "attachment-preview",
                error: {
                    message: error?.message || `Failed to preview attachment: ${title}`,
                    status: error?.status || error?.response?.status || 500
                },
                agent,
                entity,
                description: `Error previewing attachment "${title}" from ${url}`
            });

            console.log("AuditEvent (attachment preview error):", auditEvent);
            //await logEvent(auditEvent, store);

        } catch (logErr) {
            console.error("Failed to log audit event:", logErr);
        }
    }
};

const AttachmentPreview = ({ entry, selectedAttachments, toggleSelected, fhirClient }) => {
    //console.log("AttachmentPreview entry:", entry);
    //console.log("Selected attachments:", selectedAttachments);
    const [showModal, setShowModal] = useState(false);
    const [modalSrc, setModalSrc] = useState("");
    const [modalTitle, setModalTitle] = useState("");
    const [modalType, setModalType] = useState("generic");

    if (!entry) return <p>Select a document to preview.</p>;

    const resource = entry.resource;
    const sourceType = entry.sourceType;
    const contentItems = Array.isArray(resource?.content)
        ? resource.content
        : resource?.content
            ? [resource.content]
            : [];
    const containedAttachments = (resource?.contained || [])
        .flatMap((contained) => contained.content || [])
        .filter((item) => item.attachment?.url);
    const allAttachments = [...contentItems, ...containedAttachments];

    const handleClose = () => {
        setShowModal(false);
        setModalSrc("");
        setModalTitle("");
        setModalType("generic");
    };

    return (
        <div className={styles.previewContainer}>
            <h6 className={styles.documentHeader}>Documents: {resource.id}</h6>

            {allAttachments.length > 0 ? (
                <div className="list-group mb-2">
                    {allAttachments.map((item, index) => {
                        console.log("Processing attachment:", item);
                        const attachment = item.attachment || item;
                        const url = attachment?.url || "";
                        const title = attachment?.title || `Attachment ${index + 1}`;
                        const contentType = attachment?.contentType || "application/octet-stream";
                        const creationDate = attachment?.creation;
                        const resourceId = resource.id;
                        const fileType = contentType;
                        //console.log(allAttachments); //just attachment URL, contentType and title
                        //console.log(selectedAttachments); //wrapped Binary
                        //console.log(resourceId); //id of the DocumentReference or Media
                        //console.log(index);
                        const binaryUrl = attachment?.url || "";
                        const binaryId = binaryUrl.startsWith("Binary/") ? binaryUrl.split("/")[1] : null;
                        const isSelected = selectedAttachments.some((a) => a.resourceId === binaryId && a.index === index);

                        //console.log("Is selected:", isSelected);
                        const wrapped = createWrappedMessage({ sourceType, fileType, resourceId, title, resource, attachment, index });
                        //console.log("Wrapped attachment:", wrapped);

                        //const bins = getBinaryResourcesFromDocumentReference(wrapped.resource);
                        //console.log("Binary resources:", bins);

                        return (
                            <div
                                key={index}
                                className={`list-group-item d-flex flex-column ${isSelected ? styles.selectedItem : styles.attachmentItem}`}
                            >
                                <div className={styles.attachmentRow}>
                                    <input
                                        type="checkbox"
                                        className={`form-check-input ${styles.checkbox}`}
                                        checked={isSelected}
                                        onChange={() => toggleSelected(wrapped)}
                                    />
                                    {getIconForContentType(contentType, "Preview")}
                                    <span className={styles.title}>{title}</span>
                                </div>

                                <div className={styles.fileTypeLine}>
                                    Type: <span className={styles.fileType}>{sourceType}</span>
                                    {" | "}
                                    File: <span className={styles.fileType}>{fileType}</span>
                                </div>

                                {creationDate && (
                                    <div className={styles.creation}>
                                        Created: {new Date(creationDate).toLocaleString()}
                                    </div>
                                )}

                                <div className={styles.linkRow}>
                                    <button
                                        onClick={() => handleDownload(fhirClient, contentType, url, title)}
                                        className={styles.downloadLink}
                                    >
                                        Download
                                    </button>

                                    <button
                                        onClick={() =>
                                            handleOpen(
                                                fhirClient,
                                                contentType,
                                                url,
                                                title,
                                                setModalSrc,
                                                setModalTitle,
                                                setModalType,
                                                setShowModal
                                            )
                                        }
                                        className={styles.openLink}
                                    >
                                        Open
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p>No attachments found.</p>
            )}

            {/* Modal Viewer */}
            <Modal
                show={showModal}
                onHide={handleClose}
                size="xl"
                fullscreen="md-down"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ height: "80vh" }}>
                    {modalType === "pdf" && <PDFViewer blobUrl={modalSrc} />}
                    {modalType === "image" && (
                        <img
                            src={modalSrc}
                            alt={modalTitle}
                            style={{ maxWidth: "100%", maxHeight: "100%" }}
                        />
                    )}
                    {modalType === "text" && (
                        <iframe
                            src={modalSrc}
                            title={modalTitle}
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    )}
                    {modalType === "html" && (
                        <iframe
                            src={modalSrc}
                            title={modalTitle}
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    )}
                    {modalType === "generic" && (
                        <p className="text-muted text-center">Preview not supported for this file type.</p>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AttachmentPreview;